import { useEffect, useRef, type ChangeEvent } from "react";
import { NumberField } from "../NumberField";
import { audioBus } from "../audioBus";
import type { AudioFX } from "../audioFx";
import type { useAudio } from "../hooks/useAudio";

// the rack, in display order — push carries an extra "% of swarm" dial
const FX_META: Array<{ key: keyof AudioFX; label: string; hint: string }> = [
  { key: "pulse", label: "Pulse", hint: "glow + point-size swell on energy and beats" },
  { key: "push", label: "Push-away", hint: "a random set repels to the music and the swarm adapts (neighbor physics)" },
  { key: "waves", label: "Waves", hint: "bass undulation rolling across the shape (neighbor physics)" },
  { key: "ripples", label: "Ripples", hint: "each kick spawns an expanding shell (neighbor physics)" },
  { key: "swell", label: "Bass swell", hint: "the whole swarm breathes with the bass" },
  { key: "spin", label: "Bass spin", hint: "bass torques the swarm around" },
  { key: "shimmer", label: "Shimmer", hint: "treble sparkle — deeper, faster twinkle" },
  { key: "hue", label: "Hue drift", hint: "the palette wanders the hue wheel with the music" },
];

/** Three bars (bass/mid/treble) driven straight off `audioBus` via rAF — no React re-render. */
function AudioMeter() {
  const bass = useRef<HTMLDivElement>(null);
  const mid = useRef<HTMLDivElement>(null);
  const treb = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const v = audioBus.values;
      if (bass.current) bass.current.style.transform = `scaleY(${0.06 + v.bass * 0.94})`;
      if (mid.current) mid.current.style.transform = `scaleY(${0.06 + v.mid * 0.94})`;
      if (treb.current) treb.current.style.transform = `scaleY(${0.06 + v.treble * 0.94})`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  return (
    <span className="audio-meter" title="bass · mid · treble">
      <i ref={bass} />
      <i ref={mid} />
      <i ref={treb} />
    </span>
  );
}

export function AudioControls({ audio, count }: { audio: ReturnType<typeof useAudio>; count?: number }) {
  const { source, label, error, gain, setGain, fx, setFxUnit, connectSystem, connectFile, disconnect } = audio;

  const onFile = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) connectFile(f);
    e.target.value = "";
  };

  return (
    <>
      <div className="panel-row audio-row">
        {source === "none" ? (
          <>
            <button className="chip" onClick={connectSystem} title="React to the music playing on your computer">
              🔊 React to system audio
            </button>
            <label className="chip as-file">
              Track…
              <input type="file" accept="audio/*" hidden onChange={onFile} />
            </label>
            <span className="phys-note">Chrome/Edge · pick "Entire Screen" + tick "Share system audio"</span>
          </>
        ) : (
          <>
            <span className="audio-src" title={label}>
              ● {source === "system" ? "System audio" : label}
            </span>
            <AudioMeter />
            <NumberField label="Intensity" value={gain} min={0.2} max={3} step={0.1} onChange={setGain} format={(v) => v.toFixed(1) + "×"} />
            <button className="chip" onClick={disconnect}>
              ■ Stop audio
            </button>
          </>
        )}
        {error && <span className="err">{error}</span>}
      </div>

      {/* the effects rack: every music effect its own toggle + intensity */}
      {FX_META.map(({ key, label: fxLabel, hint }) => {
        const unit = fx[key];
        return (
          <div className="panel-row cym-row" key={key}>
            <button
              className={"chip toggle" + (unit.on ? " active" : "")}
              onClick={() => setFxUnit(key)({ on: !unit.on })}
              title={hint}
            >
              {fxLabel}
            </button>
            {unit.on && (
              <NumberField label="Amount" value={unit.amt} min={0} max={2} step={0.1} onChange={(v) => setFxUnit(key)({ amt: v })} format={(v) => v.toFixed(1) + "×"} />
            )}
            {unit.on && key === "push" && (
              <>
                <NumberField
                  label="Swarm %"
                  value={fx.push.frac * 100}
                  min={0.1}
                  max={50}
                  step={0.1}
                  onChange={(v) => setFxUnit("push")({ frac: v / 100 })}
                  format={(v) => v.toFixed(1) + "%"}
                />
                {count ? (
                  <NumberField
                    label="Drones"
                    value={Math.max(1, Math.round(fx.push.frac * count))}
                    min={1}
                    max={Math.round(count / 2)}
                    step={1}
                    onChange={(v) => setFxUnit("push")({ frac: v / count })}
                    format={(v) => v.toLocaleString()}
                  />
                ) : null}
              </>
            )}
          </div>
        );
      })}
    </>
  );
}
